/**
 * Getting a session back without asking, and the one attempt we are willing to spend.
 *
 * Two situations arrive here, and neither is a decision the person should be asked to
 * make. A token refresh has failed mid-session; or the app has just loaded with no token
 * at all, having been open before. The second is the ordinary case and does not look like
 * a fault: tokens live in the service worker's memory, the worker is reclaimed when the
 * browser has been idle, and MIKE Cloud's own cookie outlives all of it. So the person is
 * still signed in everywhere that matters and only our copy is gone.
 *
 * In both cases a fresh authorization request usually comes straight back with a code,
 * because that cookie is still there, and the person lands where they were. When the
 * cookie has gone too they arrive at the identity provider's own login, which is the
 * honest outcome and not one we can improve on from here.
 *
 * What this replaced in the first case was the library's `sessionLostComponent`, a screen
 * reading "Session timed out" over "Your session has expired. Please re-authenticate."
 * with no control on it at all, so the only way out was a manual reload by someone given
 * no reason to think one would help. In the second it replaced our own sign-in screen
 * naming the account, which was a real safeguard on a shared browser and is now spent
 * only when the silent attempt has already failed. See `AuthGate` for that boundary.
 *
 * A sign-out in another tab reaches neither of these. Nothing is wrong there, and
 * signing back in would undo something the person had just deliberately done.
 */

import { OidcClient } from '@axa-fr/react-oidc';
import { readAccount } from './lastAccount';
import { notifyUnauthorized } from './session';

/**
 * Marks that this browsing session has already spent its one silent attempt.
 *
 * In `sessionStorage` rather than a module variable, because the attempt is a full page
 * navigation: an in-memory flag is reset by the very redirect it exists to bound, which
 * turns one failed attempt into an endless loop between our origin and the provider.
 * `sessionStorage` is the right lifetime as well as the right survival, since a genuinely
 * new tab deserves its own attempt.
 */
const KEY = 'mikeai.silent-signin-attempted';

/** `sessionStorage`, or null where the browser refuses it. */
function defaultStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Private modes and blocked-storage settings throw on access rather than returning
    // null. Losing the guard is not worth crashing over, and the fallback below is the
    // safe direction rather than the convenient one.
    return null;
  }
}

/**
 * Whether this page has started the silent sign-in and is about to navigate away.
 *
 * A module variable on purpose: the navigation ends this page, so the flag never needs
 * to outlive it. While it is set, a refused request has nothing useful to show, because
 * anything rendered now is gone a moment later, and an error flashing up on the way out
 * reads as the send having failed. `api/http.ts` reads it for exactly that.
 */
let leaving = false;

export function isLeavingForSignIn(): boolean {
  return leaving;
}

/**
 * Start a login, which is a full-page navigation off our origin.
 *
 * A login that fails before navigating would otherwise leave the page waiting for a
 * navigation that never comes, so it falls back to the sign-in screen, which has a button.
 */
function defaultLogin(): void {
  void OidcClient.getOrThrow()
    .loginAsync()
    .catch(() => {
      leaving = false;
      notifyUnauthorized();
    });
}

/**
 * Sign in again without asking, at most once per browsing session.
 *
 * Returns whether a login was started. `false` means the caller has to show something,
 * because either the one attempt is already spent or there is nowhere to record that it
 * was spent. Without storage we decline rather than retry: a redirect loop nobody can
 * break out of is a worse failure than a sign-in screen with a button on it.
 */
export function attemptSilentSignIn(
  store: Storage | null = defaultStore(),
  login: () => void = defaultLogin,
): boolean {
  if (!store) return false;
  try {
    if (store.getItem(KEY)) return false;
    store.setItem(KEY, '1');
  } catch {
    // Full or read-only storage, which lands in the same place as no storage at all.
    return false;
  }
  leaving = true;
  login();
  return true;
}

/**
 * Give the attempt back, once a session is running again.
 *
 * Without this the guard would be spent for the lifetime of the tab, and the second
 * expiry of a long day would land on the sign-in screen even though the first recovered
 * cleanly. Called from `AuthGate` on every authenticated render, so it costs a
 * `removeItem` of a key that is usually absent.
 */
export function clearSilentSignInAttempt(store: Storage | null = defaultStore()): void {
  try {
    store?.removeItem(KEY);
  } catch {
    // As above.
  }
}

/**
 * Whether a request the renewal could not save may go round the silent sign-in.
 *
 * A page already leaving counts as handled: a failed renewal may have started the sign-in
 * through `onSessionLost`, which spends the attempt, and falling through to the sign-in
 * screen then would flash it on the way out. Otherwise only with an account still
 * remembered, the same condition `AuthGate`'s cold-start resume uses: every deliberate
 * sign-out forgets the account first, in this tab or another, and a request refused just
 * after one must end on the signed-out screen, because signing back in would undo what the
 * person had just done.
 */
export function signInAgainAfterRefusal(
  leaving: boolean = isLeavingForSignIn(),
  account: string | null = readAccount(),
  attempt: () => boolean = attemptSilentSignIn,
): boolean {
  if (leaving) return true;
  if (account === null) return false;
  return attempt();
}
